import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

/**
 * Bandeau de consentement cookies / RGPD
 * - Ne s'affiche qu'une fois par navigateur (choix persisté dans localStorage)
 * - Expiration conforme CNIL : 6 mois. Au-delà, on redemande le consentement.
 * - 3 actions : Accepter · Refuser · En savoir plus (lien vers /confidentialite)
 * - Consent Mode v2 : "denied by default" au chargement (voir index.html),
 *   puis mis à jour dès qu'un choix est enregistré.
 */
const STORAGE_KEY = "nx_cookie_consent";
const STORAGE_TS_KEY = "nx_cookie_consent_ts";
const CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 mois (recommandation CNIL)

function getStoredConsent() {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    const ts = parseInt(localStorage.getItem(STORAGE_TS_KEY) || "0", 10);
    if (!ts || Date.now() - ts > CONSENT_MAX_AGE_MS) {
      // Expiré : on efface et on redemande
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TS_KEY);
      return null;
    }
    return value;
  } catch (_) {
    return null;
  }
}

function setStoredConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
  } catch (_) {
    /* localStorage indisponible */
  }
}

function applyConsent(value) {
  if (typeof window === "undefined") return;
  // Consent Mode v2 : signal envoyé à Google Analytics (branché seulement si gtag présent)
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: value === "granted" ? "granted" : "denied",
      analytics_storage: value === "granted" ? "granted" : "denied",
      ad_user_data: value === "granted" ? "granted" : "denied",
      ad_personalization: value === "granted" ? "granted" : "denied",
    });
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Affichage retardé de 800 ms pour ne pas gêner le premier rendu du hero
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    } else {
      // Rejoue le consent au chargement pour maintenir les signaux GA cohérents
      applyConsent(stored);
    }
  }, []);

  const handle = (choice) => {
    setStoredConsent(choice);
    applyConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Consentement cookies"
      data-testid="cookie-banner"
      style={{
        position: "fixed",
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 720,
        margin: "0 auto",
        background: "rgba(13, 43, 69, 0.98)",
        color: "#fff",
        borderRadius: 12,
        boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.45)",
        border: "1px solid rgba(0, 200, 255, 0.28)",
        backdropFilter: "blur(12px)",
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, justifyContent: "space-between" }}>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.92)" }}>
          <strong style={{ color: "#00C8FF", display: "block", marginBottom: 4, fontSize: 15 }}>
            Confidentialité et cookies
          </strong>
          Nous utilisons uniquement des cookies techniques nécessaires au fonctionnement du site.
          Aucun cookie de traçage n&apos;est déposé sans votre consentement.
          {" "}
          <Link
            to="/confidentialite"
            className="hover:text-cyan"
            style={{ color: "#00C8FF", textDecoration: "underline" }}
            data-testid="cookie-banner-privacy-link"
          >
            En savoir plus
          </Link>
          .
        </div>
        <button
          type="button"
          onClick={() => handle("denied")}
          aria-label="Fermer et refuser"
          data-testid="cookie-banner-close"
          style={{
            background: "transparent",
            border: "none",
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            padding: 4,
            marginTop: -4,
            marginRight: -4,
          }}
        >
          <X size={18} strokeWidth={1.75} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => handle("granted")}
          data-testid="cookie-banner-accept"
          style={{
            flex: "1 1 auto",
            minWidth: 140,
            padding: "10px 18px",
            background: "#00C8FF",
            color: "#0D2B45",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.01em",
            transition: "filter 0.2s ease, transform 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; e.currentTarget.style.transform = "none"; }}
        >
          Tout accepter
        </button>
        <button
          type="button"
          onClick={() => handle("denied")}
          data-testid="cookie-banner-refuse"
          style={{
            flex: "1 1 auto",
            minWidth: 140,
            padding: "10px 18px",
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.35)",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            letterSpacing: "0.01em",
            transition: "border-color 0.2s ease, transform 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#00C8FF"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; e.currentTarget.style.transform = "none"; }}
        >
          Refuser
        </button>
      </div>
    </div>
  );
}
